import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCirclePlay,
  faCirclePause,
  faCircleStop,
  faPen,
  faTriangleExclamation,
  faCalendarCheck,
  faRotate,
} from '@fortawesome/free-solid-svg-icons';
import './StudentSubscription.css';

const API = process.env.REACT_APP_API_BASE_URL;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('userData')}` } });

const STATES = [
  { key: 'active', label: 'Active', icon: faCirclePlay, tone: 'is-good' },
  { key: 'paused', label: 'Paused', icon: faCirclePause, tone: 'is-warn' },
  { key: 'stopped', label: 'Stopped', icon: faCircleStop, tone: 'is-off' },
];
const toneOf = (s) => STATES.find((x) => x.key === s)?.tone || 'is-good';

const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const niceDay = (s) => {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  return m ? new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : '';
};

/* Subscription card on the student Overview: Active / Paused / Stopped.
   Pausing or stopping can keep the weekly slots reserved or free them for other students;
   freed slots are remembered and can be given back when the student returns. */
const StudentSubscription = ({ studentId, onStatus, onOpenSchedule }) => {
  const [sub, setSub] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [draft, setDraft] = useState(null); // { status, pauseUntil, note, slots, restoreSlots }
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [done, setDone] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const res = await axios.get(`${API}/api/admin/studentprofile/subscription/${studentId}`, auth());
      setSub(res.data);
      onStatus?.(res.data.status);
    } catch (err) {
      setLoadError(err.response?.data?.error || 'We couldn’t load the subscription.');
    } finally {
      setLoading(false);
    }
  }, [studentId, onStatus]);

  useEffect(() => {
    load();
  }, [load]);

  const startEdit = (status) => {
    setSaveError('');
    setDone('');
    setDraft({
      status,
      pauseUntil: status === 'paused' ? sub.pauseUntil || '' : '',
      note: status === sub.status ? sub.note : '',
      slots: 'keep',
      restoreSlots: true,
    });
  };

  const save = async () => {
    setSaving(true);
    setSaveError('');
    try {
      const res = await axios.put(
        `${API}/api/admin/studentprofile/subscription/${studentId}`,
        {
          status: draft.status,
          pauseUntil: draft.status === 'paused' && draft.pauseUntil ? draft.pauseUntil : null,
          note: draft.note,
          slots: draft.slots,
          restoreSlots: draft.restoreSlots,
        },
        auth()
      );
      const r = res.data;
      setSub(r);
      onStatus?.(r.status);
      setDraft(null);
      const bits = [];
      if (r.released) bits.push(`${r.released} ${r.released === 1 ? 'slot' : 'slots'} freed`);
      if (r.restored?.length) bits.push(`slots given back: ${r.restored.join(', ')}`);
      if (r.notRestored?.length) bits.push(`couldn’t give back: ${r.notRestored.join(', ')}`);
      setDone(`Saved${bits.length ? ` · ${bits.join(' · ')}` : ''}.`);
    } catch (err) {
      setSaveError(err.response?.data?.error || 'Couldn’t save. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading && !sub) return <section className="rmasub rmasp-card" aria-busy="true"><div className="rmasub-skel" /></section>;

  if (loadError && !sub) {
    return (
      <section className="rmasub rmasp-card">
        <p className="rmasub-error">
          <FontAwesomeIcon icon={faTriangleExclamation} /> {loadError}{' '}
          <button type="button" className="rmasub-link" onClick={load}>
            <FontAwesomeIcon icon={faRotate} /> Try again
          </button>
        </p>
      </section>
    );
  }

  const current = sub.status;
  const freeSaved = sub.savedSlots.filter((s) => s.free);
  const d = draft;
  const leaving = d && d.status !== 'active';
  const returning = d && d.status === 'active' && current !== 'active';

  return (
    <section className={`rmasub rmasp-card ${toneOf(current)}`}>
      <div className="rmasub-head">
        <h2 className="rmasp-h2">
          <FontAwesomeIcon icon={faCalendarCheck} /> Subscription
        </h2>
        <div className="rmasub-seg" role="radiogroup" aria-label="Subscription status">
          {STATES.map((s) => {
            const on = (d ? d.status : current) === s.key;
            return (
              <button
                key={s.key}
                type="button"
                role="radio"
                aria-checked={on}
                className={`${s.tone} ${on ? 'is-on' : ''}`}
                onClick={() => (s.key === current && !d ? null : s.key === current ? setDraft(null) : startEdit(s.key))}
                disabled={saving}
              >
                <FontAwesomeIcon icon={s.icon} /> {s.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* ---------- Current state ---------- */}
      {!d && (
        <div className="rmasub-now">
          {current === 'active' && (
            <p>
              Taking classes{sub.slots ? ` · weekly slots ${sub.slots}` : ' · no weekly slots picked'}.
            </p>
          )}
          {current !== 'active' && (
            <>
              <p>
                <b>{current === 'paused' ? 'Paused' : 'Stopped'}</b>
                {current === 'paused' && (sub.pauseUntil ? ` until ${niceDay(sub.pauseUntil)}` : ', no return date')}
                {sub.changedAt ? ` · since ${sub.changedAt}` : ''}
              </p>
              <p>
                {sub.slots
                  ? `Slots still reserved: ${sub.slots}`
                  : sub.savedSlots.length
                  ? `Slots freed (remembered: ${sub.savedSlots.map((s) => s.label).join(', ')})`
                  : 'No weekly slots'}
              </p>
              {sub.note && <p className="rmasub-note">“{sub.note}”</p>}
              {current === 'paused' && sub.pauseUntil && sub.pauseUntil <= todayStr() && (
                <p className="rmasub-warn">
                  <FontAwesomeIcon icon={faTriangleExclamation} /> The return date has passed. Set them Active and add their classes.
                </p>
              )}
              <button type="button" className="rmasub-link" onClick={() => startEdit(current)}>
                <FontAwesomeIcon icon={faPen} /> Edit details
              </button>
            </>
          )}
          {current !== 'active' && sub.upcoming > 0 && (
            <p className="rmasub-warn">
              <FontAwesomeIcon icon={faTriangleExclamation} />
              <span>
                {sub.upcoming} upcoming {sub.upcoming === 1 ? 'class is' : 'classes are'} still scheduled.{' '}
                {onOpenSchedule && (
                  <button type="button" className="rmasub-link" onClick={onOpenSchedule}>
                    Open Schedule to delete them
                  </button>
                )}
              </span>
            </p>
          )}
          {done && <p className="rmasub-done">{done}</p>}
        </div>
      )}

      {/* ---------- Change ---------- */}
      {d && (
        <div className="rmasub-form">
          {d.status === 'paused' && (
            <label className="rmasub-field">
              <span>Coming back on (optional)</span>
              <input type="date" value={d.pauseUntil} min={todayStr()} onChange={(e) => setDraft({ ...d, pauseUntil: e.target.value })} disabled={saving} />
              <small>You’ll get a reminder on that day.</small>
            </label>
          )}

          {leaving && (
            <label className="rmasub-field">
              <span>Note (optional)</span>
              <input type="text" maxLength={300} placeholder="Traveling, exams, …" value={d.note} onChange={(e) => setDraft({ ...d, note: e.target.value })} disabled={saving} />
            </label>
          )}

          {leaving && sub.slots && (
            <fieldset className="rmasub-choice">
              <legend>Their weekly slots: {sub.slots}</legend>
              <label className={d.slots === 'keep' ? 'is-on' : ''}>
                <input type="radio" name="rmasub-slots" checked={d.slots === 'keep'} onChange={() => setDraft({ ...d, slots: 'keep' })} disabled={saving} />
                <span>
                  <b>Keep them reserved</b>
                  <small>Nobody else can take these times.</small>
                </span>
              </label>
              <label className={d.slots === 'release' ? 'is-on' : ''}>
                <input type="radio" name="rmasub-slots" checked={d.slots === 'release'} onChange={() => setDraft({ ...d, slots: 'release' })} disabled={saving} />
                <span>
                  <b>Free them for other students</b>
                  <small>They’re remembered, so you can try to give them back later.</small>
                </span>
              </label>
            </fieldset>
          )}

          {returning && sub.savedSlots.length > 0 && (
            <div className="rmasub-restore">
              <label className="rmasub-check">
                <input
                  type="checkbox"
                  checked={d.restoreSlots}
                  onChange={(e) => setDraft({ ...d, restoreSlots: e.target.checked })}
                  disabled={saving || !freeSaved.length}
                />
                <span>Give back their old slots</span>
              </label>
              <ul>
                {sub.savedSlots.map((s) => (
                  <li key={`${s.day}-${s.slot}`} className={s.free ? 'is-good' : 'is-bad'}>
                    {s.label} · {s.free ? 'free' : s.why}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {returning && <p className="rmasub-hint">Then add their classes in the Schedule tab (the slot suggestions will be ready).</p>}

          {leaving && sub.upcoming > 0 && (
            <p className="rmasub-warn">
              <FontAwesomeIcon icon={faTriangleExclamation} /> {sub.upcoming} upcoming {sub.upcoming === 1 ? 'class is' : 'classes are'} still scheduled.
              They won’t be changed; move or delete them in the Schedule tab.
            </p>
          )}

          {saveError && <p className="rmasub-error">{saveError}</p>}

          <div className="rmasub-actions">
            <button type="button" className="rmasp-btn rmasp-btn--ghost" onClick={() => setDraft(null)} disabled={saving}>
              Cancel
            </button>
            <button type="button" className="rmasp-btn" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : d.status === current ? 'Save details' : `Set ${STATES.find((s) => s.key === d.status).label.toLowerCase()}`}
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

export default StudentSubscription;
